import { useEffect, useState } from 'react';
import { api, money, entityRange } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { ViewLink } from '../components/ViewLink.jsx';

/**
 * The landing page, for whoever is looking at it.
 *
 * The practice and the client use the same application, so this screen asks
 * the entity's CAPABILITIES what it may fetch rather than assuming a
 * bookkeeper. Requesting a trial balance as a client is not merely useless —
 * it is a 403, and the overview would render an error instead of a welcome.
 */
export function Overview({ entity, onView }) {
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);

  const caps = entity?.capabilities ?? [];
  const seesLedger = caps.includes('reports.ledger');
  const seesSummary = caps.includes('reports.summary');
  const seesDocuments = caps.includes('documents.view');
  const capturesOnly = !seesSummary && !seesLedger;

  useEffect(() => {
    if (!entity?.id) return;
    setState(null); setErr(null);

    const r = entityRange(entity);
    const want = [];
    const add = (key, promise) => want.push(promise.then((v) => [key, v]));

    if (seesLedger) {
      add('tb', api.get(`/api/reports/${entity.id}/trial-balance?from=${r.from}&to=${r.to}`));
      add('jn', api.get(`/api/entities/${entity.id}/journals?limit=8`));
    }
    if (seesSummary) {
      add('is', api.get(`/api/reports/${entity.id}/income-statement?from=${r.from}&to=${r.to}`));
      add('dr', api.get(`/api/reports/${entity.id}/age-analysis/customer?asAt=${r.to}`));
      add('cr', api.get(`/api/reports/${entity.id}/age-analysis/supplier?asAt=${r.to}`));
    }
    if (seesDocuments && !seesLedger) {
      add('docs', api.get(`/api/entities/${entity.id}/documents?limit=8`));
    }

    Promise.all(want)
      .then((pairs) => setState({ ...Object.fromEntries(pairs), range: r }))
      .catch((e) => setErr(e.message));
  }, [entity?.id]);

  if (capturesOnly) {
    return (
      <>
        <ReportHead entity={entity} title="Overview" />
        <Empty>
          <p>You are set up to capture slips for {entity?.name}.</p>
          <p style={{ marginTop: 8 }}>
            <ViewLink className="btn btn--quiet" view="slips" entityId={entity.id}
                        onNavigate={() => onView('slips')}>
              Photograph a receipt or invoice
            </ViewLink>
          </p>
        </Empty>
      </>
    );
  }

  if (err) return <><ReportHead entity={entity} title="Overview" /><div className="error">{err}</div></>;
  if (!state) return <><ReportHead entity={entity} title="Overview" /><div className="empty">Loading…</div></>;

  const off = state.tb && state.tb.totals.outOfBalance !== '0.00';

  return (
    <>
      <ReportHead entity={entity} title="Overview"
                  sub={`Financial year ${state.range.from} to ${state.range.to}`} />

      {off && (
        <div className="error">
          The trial balance is out by {money(state.tb.totals.outOfBalance)}. Investigate before
          relying on any statement for this client.
        </div>
      )}

      {state.is && (
        <div className="stat-row">
          <div className="stat">
            <div className="stat__label">Revenue, year to date</div>
            <div className="stat__value">{money(state.is.subtotals.revenue)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">Profit, year to date</div>
            <div className="stat__value">{money(state.is.subtotals.profitForPeriod)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">Owed by customers</div>
            <div className="stat__value">{money(state.dr.totals.total)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">Owed to suppliers</div>
            <div className="stat__value stat__value--credit">{money(state.cr.totals.total)}</div>
          </div>
        </div>
      )}

      {/* A bookkeeper wants the journal; a client wants their invoices. */}
      {state.jn && (
        <>
          <h2 className="eyebrow" style={{ marginBottom: 10 }}>Recent entries</h2>
          {state.jn.journals.length === 0 ? (
            <Empty>
              <p>Nothing posted yet for {entity.name}.</p>
              <p>
                <ViewLink className="btn btn--quiet" style={{ marginTop: 10 }} view="journal" entityId={entity.id}
                        onNavigate={() => onView('journal')}>
                  Capture a journal
                </ViewLink>
              </p>
            </Empty>
          ) : (
            <div className="scroll-x">
              <table className="ledger">
                <thead>
                  <tr><th style={{ width: 60 }}>Entry</th><th style={{ width: 100 }}>Date</th>
                      <th>Description</th><th>Source</th><th className="num">Value</th></tr>
                </thead>
                <tbody>
                  {state.jn.journals.map((j) => (
                    <tr key={j.id}>
                      <td className="code figure">{j.entry_no}</td>
                      <td className="figure">{j.transaction_date}</td>
                      <td>
                        {j.description}
                        {j.reversed_by && <span className="neg"> · reversed by {j.reversed_by}</span>}
                      </td>
                      <td>{j.source.replace('_', ' ')}</td>
                      <td className="num"><Money value={j.total} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {state.docs && (
        <>
          <h2 className="eyebrow" style={{ marginBottom: 10 }}>Recent documents</h2>
          {state.docs.documents.length === 0 ? (
            <Empty>
              <p>Nothing raised yet.</p>
              <p>
                <ViewLink className="btn btn--quiet" style={{ marginTop: 10 }} view="invoice" entityId={entity.id}
                        onNavigate={() => onView('invoice')}>
                  Raise an invoice
                </ViewLink>
              </p>
            </Empty>
          ) : (
            <div className="scroll-x">
              <table className="ledger">
                <thead>
                  <tr><th style={{ width: 90 }}>Number</th><th style={{ width: 100 }}>Date</th>
                      <th>Contact</th><th className="num">Total</th><th className="num">Outstanding</th></tr>
                </thead>
                <tbody>
                  {state.docs.documents.map((d) => (
                    <tr key={d.id}>
                      <td className="code figure">{d.number}</td>
                      <td className="figure">{d.document_date}</td>
                      <td>{d.counterparty_name}</td>
                      <td className="num"><Money value={d.gross} /></td>
                      <td className="num"><Money value={d.outstanding} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Add a client company. */
export function NewEntity({ onCreated }) {
  const [form, setForm] = useState({
    name: '', registrationNumber: '', isVatRegistered: false,
    vatNumber: '', vatCategory: 'B',
    financialYearEndMonth: 2, financialYearEndYear: new Date().getUTCFullYear() + 1,
  });
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({
    ...form,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  });

  const submit = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post('/api/entities', {
        ...form,
        financialYearEndMonth: Number(form.financialYearEndMonth),
        financialYearEndYear: Number(form.financialYearEndYear),
        registrationNumber: form.registrationNumber || undefined,
        vatNumber: form.isVatRegistered ? form.vatNumber : undefined,
        vatCategory: form.isVatRegistered ? form.vatCategory : undefined,
      });
      setMsg(`${r.entity.name} is ready — chart of accounts, VAT codes and ${r.fy.periods.length} periods created.`);
      onCreated(r.entity.id);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  return (
    <>
      <header className="rhead">
        <h1 className="rhead__title display">Add a client/company</h1>
        <div className="rhead__sub">
          A full chart of accounts, South African VAT codes and a financial year are created
          automatically.
        </div>
      </header>

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="form-grid">
        <label className="field"><span>Registered name</span>
          <input value={form.name} onChange={set('name')} placeholder="Acme Trading (Pty) Ltd" /></label>
        <label className="field"><span>Registration number</span>
          <input className="input--figure" value={form.registrationNumber} onChange={set('registrationNumber')}
                 placeholder="2019/123456/07" /></label>
        <label className="field"><span>Financial year end</span>
          <select value={form.financialYearEndMonth} onChange={set('financialYearEndMonth')}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select></label>
        <label className="field"><span>First year ending</span>
          <input className="input--figure" type="number" value={form.financialYearEndYear}
                 onChange={set('financialYearEndYear')} /></label>
      </div>

      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, margin: '4px 0 14px' }}>
        <input type="checkbox" checked={form.isVatRegistered} onChange={set('isVatRegistered')}
               style={{ width: 16, height: 16 }} />
        <span style={{ letterSpacing: 0, textTransform: 'none', fontSize: 14, color: 'var(--ink)' }}>
          Registered for VAT
        </span>
      </label>

      {form.isVatRegistered && (
        <>
          <div className="form-grid">
            <label className="field"><span>VAT number</span>
              <input className="input--figure" value={form.vatNumber} onChange={set('vatNumber')} placeholder="4123456789" /></label>
            <label className="field"><span>VAT category</span>
              <select value={form.vatCategory} onChange={set('vatCategory')}>
                <option value="A">A — two-monthly, ending odd months</option>
                <option value="B">B — two-monthly, ending even months</option>
                <option value="C">C — monthly</option>
                <option value="D">D — six-monthly (farming)</option>
                <option value="E">E — annual</option>
              </select></label>
          </div>
          <div className="note">
            The category sets the tax period calendar. It is on the client's VAT 103
            registration certificate.
          </div>
        </>
      )}

      <button className="btn" style={{ marginTop: 16 }} onClick={submit} disabled={busy || !form.name}>
        {busy ? 'Creating' : 'Create client'}
      </button>
    </>
  );
}
